<template>
    <!-- 完整的图表布局容器 -->
    <div class="charts-container">
        <!-- 原始波形图区域 -->
        <div class="original-charts-section">
            <h3 class="section-title">原始波形图</h3>
            <div>
                <div class="chart-item" ref="waveChartContainers2"></div>
                <div class="chart-item" ref="waveChartContainers1"></div>
            </div>
        </div>

        <!-- 如果没有数据，显示提示 -->
        <div v-if="!waveData" class="no-data-tip">
            等待数据加载...
        </div>
    </div>
</template>

<script>
import echarts from "echarts";

export default {
    name: 'waveChart',
    props: {
        id: {
            type: String,
            default: ''
        },
        waveData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            // 将单个图表数据改为图表数据数组
            currentPointArray: [],
            voltagePointArray: [],
            chartData: [],
            // 图表实例
            charts: [],
            // 防抖定时器
            resizeTimer: null
        }
    },
    watch: {
        waveData: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.currentPointArray && newVal.voltagePointArray) {
                    // 原始数据保持不变（用于原始波形图）
                    this.currentPointArray = newVal.currentPointArray;
                    this.voltagePointArray = newVal.voltagePointArray;

                    // 数据变化时重新渲染图表
                    this.$nextTick(() => {
                        this.renderCharts();
                    });
                }
            }
        }
    },
    mounted() {
        // 组件挂载后直接渲染图表，不再获取数据
        this.renderCharts();
        // 添加窗口大小变化监听
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        // 组件销毁前移除监听事件
        window.removeEventListener('resize', this.handleResize);
        // 销毁图表实例
        this.charts.forEach(chart => {
            if (chart) {
                chart.dispose();
            }
        });
    },
    methods: {

        // 提取数组中的值（去除索引）
        extractValues(dataArray) {
            return dataArray.map(item => {
                // 如果是二维数组 [索引, 值]，返回值部分
                if (Array.isArray(item) && item.length === 2) {
                    return item[1];
                }
                // 否则直接返回
                return item;
            });
        },

        // 渲染所有图表 - 同时渲染原始图表和周期图表
        async renderCharts() {
            // 如果已有图表实例，先销毁
            this.charts.forEach(chart => {
                if (chart) {
                    chart.dispose();
                }
            });
            this.charts = [];

            // 等待DOM更新完成
            await this.$nextTick();

            // 显示第二个图表容器
            if (this.$refs.waveChartContainers2) {
                this.$refs.waveChartContainers2.style.display = 'block';
            }

            // 渲染电流双Y轴图表
            if (this.$refs.waveChartContainers1 && this.currentPointArray.length > 0) {
                const currentChart = echarts.init(this.$refs.waveChartContainers1);
                const currentOptions = this.createCurrentDualAxisOption();
                currentChart.setOption(currentOptions);
                this.charts.push(currentChart);
            }

            // 渲染电压双Y轴图表
            if (this.$refs.waveChartContainers2 && this.voltagePointArray.length > 0) {
                const voltageChart = echarts.init(this.$refs.waveChartContainers2);
                const voltageOptions = this.createVoltageDualAxisOption();
                voltageChart.setOption(voltageOptions);
                this.charts.push(voltageChart);
            }

            // 确保所有图表都已初始化后再进行连接
            this.$nextTick(() => {
                if (this.charts.length >= 2) { // 确保至少有两个图表被初始化
                    echarts.connect(this.charts); // 连接所有图表
                }
            });
        },

        creatOption(data, index) {
            // 为不同图表设置不同颜色，便于区分
            const colors = [
                "#9c9c00", "#009c9c", "#9c009c", "#9c5000",
                "#00509c", "#50009c", "#9c9c9c", "#509c00",
                "#9c0000", "#009c00", "#00009c", "#9c5050"
            ];

            const option = {
                backgroundColor: "#f5f5f5",
                title: {
                    text: index == 0 ? "电流波形" : "电压波形", // 标题文本
                    left: "center", // 标题水平居中
                    textStyle: {
                        color: "#373b47", // 标题颜色
                        fontSize: 14, // 标题字体大小，稍微调小以适应小屏幕
                        fontWeight: "normal", // 标题字体粗细
                    },
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'cross' }
                },
                axisPointer: {
                    link: [
                        {
                            xAxisIndex: "all",
                        },
                    ],
                },
                visualMap: [{
                    show: false,
                    type: "continuous",
                    //seriesIndex: index,
                    min: 0,
                    max: 400,
                }],
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '15%',
                    containLabel: true
                },
                xAxis: [{
                    name: "",
                    minorTick: {
                        show: true,
                    },
                    minorSplitLine: {
                        show: true,
                    },
                    axisLabel: {
                        color: colors[index], // 修改X轴标签文字颜色为红色
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#373b47", // x 轴的颜色
                        },
                    },
                    splitLine: {
                        show: false,
                    }
                }],
                yAxis: [{
                    type: "value",
                    axisLabel: {
                        color: "#373b47", // 修改X轴标签文字颜色
                    },
                    axisLine: {
                        // 设置Y轴线的样式
                        lineStyle: {
                            color: "#373b47",
                        },
                    },
                    splitLine: {
                        show: false,
                    },
                }],
                dataZoom: [{
                    type: "inside", // 内缩放
                    start: 0, // 初始缩放比例
                    end: 100, // 结束缩放比例 
                }, {
                    type: "slider", // 滚动条
                    show: true, // 是否显示滚动条
                    start: 0, // 初始滚动条位置
                    end: 100, // 结束滚动条位置
                }],
                series: [{
                    name: index == 0 ? "电流" : "电压",
                    type: "line",
                    showSymbol: false,
                    data: data,
                    clip: true,
                    lineStyle: {
                        color: colors[index], // 折线的颜色
                        width: 2,
                    },
                }]
            }

            return option;
        },

        // 创建电流双Y轴图表配置
        createCurrentDualAxisOption() {
            const option = {
                backgroundColor: "#f5f5f5",
                title: {
                    text: "电流波形",
                    left: "center",
                    textStyle: {
                        color: "#373b47",
                        fontSize: 14,
                        fontWeight: "normal"
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'cross'
                    }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '15%',
                    containLabel: true
                },
                xAxis: [{
                    name: "时间",
                    nameGap: 30,
                    minorTick: { show: true },
                    minorSplitLine: { show: true },
                    axisLabel: { color: "#373b47" },
                    axisLine: { lineStyle: { color: "#373b47" } },
                    splitLine: { show: false }
                }],
                yAxis: [
                    {
                        type: "value",
                        min: 0,
                        max: 5000,
                        name: "电流(mA)",
                        nameTextStyle: { color: "#e74c3c" },
                        axisLabel: {
                            color: "#e74c3c",
                            formatter: '{value}'
                        },
                        axisLine: {
                            lineStyle: {
                                color: "#e74c3c"
                            }
                        },
                        splitLine: { show: false }
                    }
                ],
                dataZoom: [{
                    type: "inside",
                    start: 0,
                    end: 100
                }, {
                    type: "slider",
                    show: true,
                    start: 0,
                    end: 100
                }],
                series: [
                    {
                        name: "电流",
                        type: "line",
                        showSymbol: false,
                        data: this.currentPointArray,
                        clip: true,
                        lineStyle: {
                            color: "#e74c3c",
                            width: 2
                        },
                        itemStyle: {
                            color: "#e74c3c"
                        },
                        yAxisIndex: 0
                    }
                ]
            };

            return option;
        },

        // 创建电压双Y轴图表配置
        createVoltageDualAxisOption() {
            const option = {
                backgroundColor: "#f5f5f5",
                title: {
                    text: "电压波形",
                    left: "center",
                    textStyle: {
                        color: "#373b47",
                        fontSize: 14,
                        fontWeight: "normal"
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'cross' }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '15%',
                    containLabel: true
                },
                xAxis: [{
                    name: "时间",
                    nameGap: 30,
                    minorTick: { show: true },
                    minorSplitLine: { show: true },
                    axisLabel: { color: "#373b47" },
                    axisLine: { lineStyle: { color: "#373b47" } },
                    splitLine: { show: false }
                }],
                yAxis: [
                    {
                        type: "value",
                        name: "电压(V)",
                        nameTextStyle: { color: "#27ae60" },
                        axisLabel: {
                            color: "#27ae60",
                            formatter: '{value}'
                        },
                        axisLine: {
                            lineStyle: {
                                color: "#27ae60"
                            }
                        },
                        splitLine: { show: false }
                    }
                ],
                dataZoom: [{
                    type: "inside",
                    start: 0,
                    end: 100
                }, {
                    type: "slider",
                    show: true,
                    start: 0,
                    end: 100
                }],
                series: [
                    {
                        name: "电压",
                        type: "line",
                        showSymbol: false,
                        data: this.voltagePointArray,
                        clip: true,
                        lineStyle: {
                            color: "#27ae60",
                            width: 2
                        },
                        itemStyle: {
                            color: "#27ae60"
                        },
                        yAxisIndex: 0
                    }
                ]
            };

            return option;
        },

        // 处理窗口大小变化
        handleResize() {
            // 清除之前的定时器
            if (this.resizeTimer) {
                clearTimeout(this.resizeTimer);
            }
            // 添加防抖，避免频繁resize
            this.resizeTimer = setTimeout(() => {
                // 重新调整所有图表大小
                this.charts.forEach(chart => {
                    if (chart) {
                        chart.resize();
                    }
                });
            }, 300);
        }
    }
}
</script>

<style scoped>
/* 完整的图表容器 */
.charts-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 32px;
}

/* 区域标题 */
.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #373b47;
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 2px solid #e8e8e8;
}

/* 原始图表区域 */
.original-charts-section {
    width: 100%;
}

/* 周期图表区域 */
.cycle-charts-section {
    width: 100%;
}

/* 周期图表网格 - CSS Grid实现等宽布局 */
.cycle-charts-grid {
    display: grid;
    gap: 2px;
    width: 100%;
    grid-auto-rows: 250px;
}

/* 周期图表项 - 等宽显示 */
.cycle-chart-item {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    padding: 10px;
    box-sizing: border-box;
    position: relative;
    min-height: 250px;
}

/* 添加网格布局样式 */
.charts-grid {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: flex-start;
    flex-wrap: wrap;
}

/* 单个图表项样式 */
.chart-item {
    height: 320px;
    flex: 1;
    min-width: 400px;
    /* 设置最小宽度防止挤扁 */
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    padding: 10px;
    transition: all 0.3s ease;
    position: relative;
    width: 100%;
    box-sizing: border-box;
}

/* 图表项悬停效果 */
.chart-item:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #d0d0d0;
}

/* 图表标题样式 */
.chart-title {
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 14px;
    font-weight: 500;
    color: #373b47;
    background: rgba(255, 255, 255, 0.9);
    padding: 4px 12px;
    border-radius: 4px;
    z-index: 10;
}

/* 无数据提示样式 */
.no-data-tip {
    width: 100%;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #999;
    background: #f5f5f5;
    border-radius: 8px;
    border: 2px dashed #ddd;
}

/* 响应式布局优化 */
@media screen and (max-width: 1200px) {
    .charts-grid {
        gap: 16px;
    }

    .chart-item {
        min-width: 350px;
        height: 280px;
    }

    .section-title {
        font-size: 16px;
    }
}

@media screen and (max-width: 768px) {
    .charts-container {
        gap: 24px;
    }

    .charts-grid {
        flex-direction: column;
        gap: 12px;
    }

    .chart-item {
        min-width: 100%;
        height: 250px;
    }

    .section-title {
        font-size: 16px;
        margin-bottom: 12px;
    }

    /* 小屏幕上周期图表调整为垂直堆叠 */
    .cycle-charts-grid {
        gap: 12px;
        grid-auto-rows: 200px;
    }

    .cycle-chart-item {
        min-height: 200px;
    }
}

@media screen and (max-width: 480px) {
    .charts-container {
        gap: 20px;
    }

    .chart-item {
        height: 220px;
        padding: 12px;
    }

    .section-title {
        font-size: 14px;
        padding-bottom: 6px;
    }

    .chart-title {
        font-size: 12px;
        padding: 2px 8px;
    }

    /* 超小屏幕上周期图表调整尺寸 */
    .cycle-charts-grid {
        gap: 10px;
        grid-auto-rows: 180px;
    }

    .cycle-chart-item {
        min-height: 180px;
    }
}
</style>